// Plan 23b, Task 3.3 — Turn Banner primitive
//
// Full-width horizontal banner for turn start announcements.
// Slides in from left, holds visibly, slides out to right, then auto-detaches.
//
// Visual structure:
//   Banner div `ui-turn-banner` — position:fixed, left:0, right:0, height ~80px
//     Centered flex row:
//       ├── (optional) Icon span `ui-turn-banner__icon` — 32px, only if iconKind provided
//       └── Text span `ui-turn-banner__text` — "Player X's Turn!"

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** No required fields — placeholder for future per-instance overrides. */
export type TurnBannerOpts = {
  /** Reserved for future per-instance overrides. Currently unused. */
  _?: never;
};

export interface ShowOpts {
  /** Player's display name, e.g. "Alice" → "Alice's Turn!" */
  playerName: string;
  /** Hex color for the banner gradient, e.g. '#E04040' */
  playerColor: string;
  /** Optional icon kind — renders a 32px dice emoji next to text */
  iconKind?: 'dice';
  /** Optional compact player icon text shown in the right badge. */
  iconText?: string;
}

export interface TurnBannerHandle extends UIComponentHandle {
  /** Mount and animate the banner for the given player. */
  show(opts: ShowOpts): void;
  /** Trigger immediate exit animation if the banner is currently visible. */
  hide(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.family.headline */
const FONT_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.turn-banner = 42px */
const FONT_SIZE = 'clamp(34px, 5vw, 58px)';

/** color.text.primary = #FFFFFF */
const TEXT_COLOR = '#FFFFFF';

/**
 * typography.shadow.title = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
 */
const TEXT_SHADOW =
  '0 3px 0 #071725, 0 0 0 #071725, 0 7px 14px rgba(0,0,0,0.55), 0 0 16px rgba(28,220,255,0.65)';

/**
 * animation.duration.normal = 250ms = 0.25s
 * Hold duration = 1000ms = 1.0s
 */
const ANIM_DURATION = 0.25;
const HOLD_DURATION = 1.0;

/**
 * Animation timeline absolute positions:
 *   t=0:    entrance begins (translateX -100% → 0)
 *   t=0.25: entrance ends, hold begins
 *   t=1.25: hold ends, exit begins (translateX 0 → 100%)
 *   t=1.5:  exit ends, auto-detach fires
 */
const T_EXIT_START = ANIM_DURATION + HOLD_DURATION; // 1.25s
const T_EXIT_END = T_EXIT_START + ANIM_DURATION; // 1.5s
export const TURN_BANNER_TOTAL_SECONDS = T_EXIT_END;

/**
 * GSAP easing — animation.easing.snappy is cubic-bezier(0.2, 0, 0, 1).
 * GSAP 3 does NOT parse raw cubic-bezier(...) strings — use closest stock ease.
 * 'power2.inOut' is a reasonable approximation for snappy feel.
 */
const EASE_SNAPPY = 'power2.inOut';

/** Exit-only animation duration (for hide()): same as entrance duration */
const HIDE_EXIT_END = ANIM_DURATION; // 0.25s

/** z-index: notification tier (500) — above all game HUD + menus */
const Z_INDEX = 500;

const BANNER_CYAN = '#18D8F4';
const BANNER_TEAL = '#0AA6C8';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createTurnBanner(config: UIComponentConfig & TurnBannerOpts): TurnBannerHandle {
  const { gsapRegistry } = config;
  const mountTarget = config.mount ?? document.body;

  let disposed = false;
  let isMounted = false;

  /** In-flight animation handle; at most one active at a time. */
  let currentHandle: GsapTimelineHandle | null = null;

  // ── Build DOM ────────────────────────────────────────────────────────────────

  // Root banner element
  const banner = document.createElement('div');
  banner.className = 'ui-turn-banner';
  banner.style.cssText = [
    'position: fixed',
    'left: -8vw',
    'right: -8vw',
    'top: 50%',
    'height: clamp(86px, 14vw, 132px)',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `z-index: ${Z_INDEX}`,
    'pointer-events: none',
    'user-select: none',
    'overflow: visible',
    'border-top: 4px solid rgba(255,255,255,0.86)',
    'border-bottom: 4px solid rgba(255,255,255,0.86)',
    'box-shadow: 0 0 18px rgba(24,216,244,0.65), 0 18px 42px rgba(0,0,0,0.42)',
    'clip-path: polygon(6% 0, 100% 0, 94% 100%, 0% 100%)',
  ].join('; ');

  // Inner flex row
  const row = document.createElement('div');
  row.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'gap: clamp(14px, 3vw, 28px)',
    'transform: rotate(-3deg)',
  ].join('; ');

  // Text span — created once, text content updated in show()
  const textEl = document.createElement('span');
  textEl.className = 'ui-turn-banner__text';
  textEl.style.cssText = [
    `font-family: ${FONT_FAMILY}`,
    `font-size: ${FONT_SIZE}`,
    `color: ${TEXT_COLOR}`,
    `text-shadow: ${TEXT_SHADOW}`,
    'font-weight: 900',
    'line-height: 1',
    '-webkit-text-stroke: 2px #071725',
    'letter-spacing: 0',
  ].join('; ');

  row.appendChild(textEl);
  banner.appendChild(row);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /** Remove banner from DOM and mark as unmounted. */
  function removeFromParent(): void {
    if (banner.parentNode) {
      banner.parentNode.removeChild(banner);
    }
    isMounted = false;
  }

  /** Kill in-flight timeline handle. */
  function killCurrent(): void {
    if (currentHandle) {
      currentHandle.dispose();
      currentHandle = null;
    }
  }

  /** Update the banner's horizontal gradient background for the given playerColor. */
  function applyPlayerColor(playerColor: string): void {
    banner.dataset.playerColor = playerColor;
    banner.style.setProperty('--turn-banner-player-color', playerColor);
    banner.style.backgroundImage = [
      `linear-gradient(100deg, transparent 0%, rgba(4,14,24,0.72) 9%, rgba(4,14,24,0.92) 26%, rgba(4,14,24,0.92) 74%, rgba(4,14,24,0.72) 91%, transparent 100%)`,
      `linear-gradient(100deg, transparent 0%, ${BANNER_TEAL} 10%, ${BANNER_CYAN} 48%, #A8F8FF 53%, ${BANNER_CYAN} 58%, ${BANNER_TEAL} 90%, transparent 100%)`,
      `linear-gradient(90deg, transparent 0%, ${playerColor} 45%, ${playerColor} 55%, transparent 100%)`,
    ].join(', ');
  }

  /** Rebuild the icon span (or clear it) based on iconKind. */
  function syncIcon(iconKind?: 'dice', iconText?: string, playerColor?: string): void {
    // Remove any prior icon
    const prior = row.querySelector('.ui-turn-banner__icon');
    if (prior) {
      row.removeChild(prior);
    }

    if (iconKind === 'dice' || iconText != null) {
      const iconEl = document.createElement('span');
      iconEl.className = 'ui-turn-banner__icon';
      iconEl.style.cssText = [
        'width: clamp(48px, 7vw, 72px)',
        'height: clamp(48px, 7vw, 72px)',
        'border-radius: 50%',
        'display: inline-flex',
        'align-items: center',
        'justify-content: center',
        'font-size: clamp(22px, 3vw, 34px)',
        'font-family: Arial Black, sans-serif',
        'line-height: 1',
        'color: #ffffff',
        'background: radial-gradient(circle at 35% 30%, rgba(255,255,255,0.55), transparent 28%), ' +
          `${playerColor ?? BANNER_CYAN}`,
        'border: 4px solid #ffffff',
        'box-shadow: 0 4px 0 #071725, 0 0 16px rgba(24,216,244,0.7)',
        'flex: 0 0 auto',
      ].join('; ');
      iconEl.textContent = iconText ?? 'D';
      row.appendChild(iconEl);
    }
  }

  // ── show() ───────────────────────────────────────────────────────────────────

  function show(opts: ShowOpts): void {
    if (disposed) return;

    const { playerName, playerColor, iconKind, iconText } = opts;

    // Kill any in-flight timeline
    killCurrent();

    // Update text and gradient
    textEl.textContent = `${playerName}'s Turn!`;
    applyPlayerColor(playerColor);
    syncIcon(iconKind, iconText, playerColor);

    // Mount banner if not already in DOM. GSAP owns the full transform so the
    // vertical centering and horizontal travel cannot fight each other.
    if (!isMounted) {
      mountTarget.appendChild(banner);
      isMounted = true;
    }

    // Build timeline
    // F20a-CLASS-1: all tl.to() / tl.fromTo() / tl.call() use explicit numeric position
    const handle = gsapRegistry.create('turn-banner:show');
    currentHandle = handle;
    const tl = handle.timeline;

    // Entrance: offscreen left → centered. Keep yPercent fixed through every
    // tween so later xPercent updates never drop the vertical centering.
    tl.fromTo(
      banner,
      { xPercent: -100, yPercent: -50 },
      {
        xPercent: 0,
        yPercent: -50,
        duration: ANIM_DURATION,
        ease: EASE_SNAPPY,
        overwrite: true,
      },
      0,
    );

    // Exit: translateX(0) → translateX(100%) over ANIM_DURATION, starting at T_EXIT_START.
    tl.to(
      banner,
      { xPercent: 100, yPercent: -50, duration: ANIM_DURATION, ease: EASE_SNAPPY },
      T_EXIT_START,
    );

    // Auto-detach after exit completes
    tl.call(removeFromParent, undefined, T_EXIT_END);
  }

  // ── hide() ───────────────────────────────────────────────────────────────────

  function hide(): void {
    if (disposed) return;
    if (!isMounted) return;

    // Kill any in-flight timeline
    killCurrent();

    // Exit-only animation
    const handle = gsapRegistry.create('turn-banner:hide');
    currentHandle = handle;
    const tl = handle.timeline;

    tl.to(banner, { xPercent: 100, yPercent: -50, duration: ANIM_DURATION, ease: EASE_SNAPPY }, 0);

    tl.call(removeFromParent, undefined, HIDE_EXIT_END);
  }

  // ── dispose() ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    killCurrent();
    removeFromParent();
  }

  return {
    element: banner,
    show,
    hide,
    dispose,
  };
}
